import { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'

import TextInput from 'components/Common/Form/Input/TextInput'
import { useCreateTripItem } from 'tripPlanner/hooks/api/tripItem'
import type { FullTrip } from 'tripPlanner/types/common'
import { isRouteItem, startPlaceOfTripItem } from 'tripPlanner/components/TripMaps/utils'
import debounce from 'lodash.debounce'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import LineSpinnerIcon from 'LuxKit/Icons/line/LineSpinnerIcon'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import Hr from 'components/Common/Hr'
import useArrowListNavigation from 'hooks/useArrowListNavigation'
import noop from 'lib/function/noop'
import LineNotesIcon from 'LuxKit/Icons/line/LineNotesIcon'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ExampleFlipper from './ExampleFlipper'
import { trackClientEvent } from 'analytics/analytics'
import { useView } from 'tripPlanner/hooks/View/useView'
import { useIntelligentTripSuggestions } from 'tripPlanner/hooks/useIntelligentTripSuggestions'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import MarkdownRender from 'LuxKit/MarkdownRender'
import type { CreateTripItemData, CreateTripItemRankSpec } from 'tripPlanner/api/tripItem/types'
import type { definitions } from '@luxuryescapes/contract-trip'
import { getGooglePlaceAutocomplete, getGooglePlaceDetail } from 'tripPlanner/api/places'
import { scrollToNewItemWithRetry } from 'tripPlanner/utils/scrollToNewItem'
import { buildTripItemFromFields } from 'tripPlanner/utils/buildTripItemFromFields'

const PLACE_BIAS_RADIUS_DEGREES = 0.5

const SearchIcon = styled(LineSearchIcon)`
  margin-right: ${rem(8)};
`

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

const LoadingIcon = styled(LineSpinnerIcon)`
  margin-right: ${rem(8)};
  animation: ${spin} 1s linear infinite;
`

const PreSpan = styled.span`
  white-space: pre-wrap;
`

const StyledExampleFlipper = styled(ExampleFlipper)`
  pointer-events: none;
  display: none;
  position: absolute;
  top: 50%;
  left: ${rem(14)};
  transform: translateY(-50%);
`

const InputWrapper = styled.div`
  position: relative;

  &:has(input:placeholder-shown) {
    ${StyledExampleFlipper} {
      display: block;
    }
  }
`

function determineBounds(trip: FullTrip) {
  if (!google.maps) {
    return undefined
  }
  const bounds = new google.maps.LatLngBounds()
  let hasGeoPlaces = false
  trip.items.forEach((item) => {
    if (item.type === 'NOTE' || isRouteItem(item)) {
      return
    }

    const startPlace = startPlaceOfTripItem(item)

    if (startPlace?.type === 'GEO') {
      bounds.extend(
        new google.maps.LatLng(startPlace.point.lat, startPlace.point.lng),
      )
      hasGeoPlaces = true
    }

    if ('endPlace' in item && item.endPlace?.type === 'GEO' && item.endPlace.point) {
      bounds.extend(
        new google.maps.LatLng(
          item.endPlace.point.lat,
          item.endPlace.point.lng,
        ),
      )
      hasGeoPlaces = true
    }
  })

  if (!hasGeoPlaces) {
    return undefined
  }

  // https://developers.google.com/maps/documentation/places/web-service/autocomplete#locationbias
  return `rectangle:${bounds.getSouthWest().lat()},${bounds.getSouthWest().lng()}|${bounds.getNorthEast().lat()},${bounds.getNorthEast().lng()}`
}

function getLastKnownPlaceName(trip: FullTrip): string | undefined {
  for (let i = trip.items.length - 1; i >= 0; i--) {
    const item = trip.items[i]
    if (item.type === 'NOTE' || isRouteItem(item)) {
      continue
    }

    if ('endPlace' in item && item.endPlace?.name) return item.endPlace.name

    if ('place' in item && item.place?.name) return item.place.name

    if ('startPlace' in item && item.startPlace?.name) return item.startPlace.name
  }
  return undefined
}

function getLastKnownPlaceCoordinates(trip: FullTrip): { lat: number, lng: number } | undefined {
  for (let i = trip.items.length - 1; i >= 0; i--) {
    const item = trip.items[i]
    if (item.type === 'NOTE' || isRouteItem(item)) {
      continue
    }

    if ('endPlace' in item && item.endPlace?.type === 'GEO' && item.endPlace.point) {
      return { lat: item.endPlace.point.lat, lng: item.endPlace.point.lng }
    }

    if ('place' in item && item.place?.type === 'GEO' && item.place.point) {
      return { lat: item.place.point.lat, lng: item.place.point.lng }
    }

    if ('startPlace' in item && item.startPlace?.type === 'GEO' && item.startPlace.point) {
      return { lat: item.startPlace.point.lat, lng: item.startPlace.point.lng }
    }
  }
  return undefined
}

function getContextualPlaceBias(trip: FullTrip, date?: string): string | undefined {
  if (date) {
    const targetDate = new Date(date).toDateString()
    const sameDayItems = trip.items.filter(item => {
      if (item.type === 'NOTE' || isRouteItem(item)) return false
      const itemDate = item.startDate ? new Date(item.startDate.toDate()).toDateString() : null
      return itemDate === targetDate
    })

    if (sameDayItems.length > 0) {
      const firstItem = sameDayItems[0]
      let centerPoint: { lat: number, lng: number } | undefined

      if ('place' in firstItem && firstItem.place?.type === 'GEO' && firstItem.place.point) {
        centerPoint = firstItem.place.point
      } else if ('endPlace' in firstItem && firstItem.endPlace?.type === 'GEO' && firstItem.endPlace.point) {
        centerPoint = firstItem.endPlace.point
      } else if ('startPlace' in firstItem && firstItem.startPlace?.type === 'GEO' && firstItem.startPlace.point) {
        centerPoint = firstItem.startPlace.point
      }

      if (centerPoint) {
        const lat = centerPoint.lat
        const lng = centerPoint.lng
        return `rectangle:${lat - PLACE_BIAS_RADIUS_DEGREES},${lng - PLACE_BIAS_RADIUS_DEGREES}|${lat + PLACE_BIAS_RADIUS_DEGREES},${lng + PLACE_BIAS_RADIUS_DEGREES}`
      }
    }
  }

  // Fall back to trip-wide bounds (if no date provided or no items on that specific date)
  // This could span multiple cities/countries - appropriate for "Add to Trip" button with no date
  return determineBounds(trip)
}

function getListItemId(index: number) {
  return `nlp-item-create-option-${index}`
}
const INPUT_ID = 'nlp-item-create-input'
const ADD_NOTE_ID = 'nlp-item-create-add-note'

interface Props {
  trip: FullTrip
  date?: string
  onItemCreated?: (item: unknown) => void
  dropdownAutoHeight?: boolean
  rankSpec?: CreateTripItemRankSpec
}

function toTripPlace(place?: { name?: string, secondaryText?: string, point?: { lat: number, lng: number }, placeId?: string }): definitions['place'] | undefined {
  if (!place?.name) return undefined
  if (place.point && typeof place.point.lat === 'number' && typeof place.point.lng === 'number') {
    return {
      type: 'GEO' as const,
      name: place.name,
      point: { lat: place.point.lat, lng: place.point.lng },
      address: place.secondaryText,
      id: place.placeId,
    }
  }
  return { type: 'CUSTOM' as const, name: place.name, address: place.secondaryText }
}

interface ParsedFieldsInput {
  itemType?: string
  toPlace?: { name?: string, secondaryText?: string, point?: { lat: number, lng: number }, airportCode?: string, searchTerm?: string, placeId?: string }
  fromPlace?: { name?: string, secondaryText?: string, point?: { lat: number, lng: number }, airportCode?: string, searchTerm?: string, placeId?: string }
  startDate?: string
  endDate?: string
  startTime?: string
  endTime?: string
  tripItemName?: string
  suggestedTitle?: string
  shortActivityDescription?: string
  flightNumber?: string
  airlineName?: string
  originAirportCode?: string
  destinationAirportCode?: string
}

function mapParsedFieldsToRequest(pf: ParsedFieldsInput, defaultName: string): CreateTripItemData | undefined {
  if (!pf?.itemType) return undefined

  const name = pf.tripItemName || pf.suggestedTitle || pf.shortActivityDescription || defaultName

  return buildTripItemFromFields(
    {
      itemType: pf.itemType,
      name,
      startDate: pf.startDate,
      endDate: pf.endDate,
      startTime: pf.startTime,
      endTime: pf.endTime,
      flightNumber: pf.flightNumber,
      airlineName: pf.airlineName,
    },
    {
      from: toTripPlace(pf.fromPlace),
      to: toTripPlace(pf.toPlace),
      originAirportCode: pf.originAirportCode || pf.fromPlace?.airportCode,
      destinationAirportCode: pf.destinationAirportCode || pf.toPlace?.airportCode,
    },
  )
}

function NLPItemCreate({ trip, date, rankSpec, onItemCreated = noop, dropdownAutoHeight = false }: Props) {
  const intl = useIntl()
  const inputElement = useRef<HTMLInputElement>(null)

  const placeBias = useMemo(() => getContextualPlaceBias(trip, date), [trip, date])
  const lastKnownPlace = useMemo(() => {
    return trip.destinationsGeo?.[0]?.lePlaceName ||
      trip.destinationsGeo?.[0]?.shortName ||
      trip.originGeo?.lePlaceName ||
      getLastKnownPlaceName(trip)
  }, [trip])
  const lastKnownCoordinates = useMemo(() => getLastKnownPlaceCoordinates(trip), [trip])

  const [currentMessage, setCurrentMessage] = useState('')

  const interactionEventSent = useRef(false)

  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [creatingOptionIndex, setCreatingOptionIndex] = useState<number | null>(null)

  const closeDropdown = useCallback(() => {
    setDropdownOpen(false)
  }, [])

  const openDropdown = useCallback(() => {
    setDropdownOpen(true)
  }, [])

  const activeTripDateISO = date
  const { data, options, suggestions, loading, error } = useIntelligentTripSuggestions(currentMessage, {
    activeTripDateISO,
    coordinates: trip.destinationsGeo?.[0]?.point || trip.originGeo?.point || lastKnownCoordinates,
    placeBias,
    lastKnownPlace,
    homeAirportCode: undefined,
  })

  useEffect(() => {
    if ((loading || options.length > 0 || suggestions.length > 0) && currentMessage.length >= 3) {
      openDropdown()
    }
  }, [loading, options.length, suggestions.length, currentMessage.length, openDropdown])

  const { mutate: createTripItem, isLoading: isCreatingTripItem } = useCreateTripItem({
    onSuccess: (item) => {
      trackClientEvent({
        subject: item.type,
        action: currentMessage,
        category: 'save_ai_item',
        type: 'interaction',
      })
      scrollToNewItemWithRetry(item.id)
      closeDropdown()
      onItemCreated(item)
      setCreatingOptionIndex(null)
    },
    onError: () => {
      setCreatingOptionIndex(null)
      showSnackbar(intl.formatMessage({
        id: 'sCAgGlYEMurJ7keCipUh',
        defaultMessage: 'Failed to create item',
        description: 'Error message when a trip item could not be created',
      }), 'critical')
    },
  })

  const onInputChange = useMemo(() => debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    const inputMessage = e.target.value
    setCurrentMessage(inputMessage)
    if (inputMessage.length > 0) {
      setDropdownOpen(true)
      if (!interactionEventSent.current) {
        trackClientEvent({
          subject: 'opened_ai_input',
          action: 'opened_ai_input',
          category: 'trip_planner',
          type: 'interaction',
        })
        interactionEventSent.current = true
      }
    } else {
      setDropdownOpen(false)
    }
  }, 300), [])

  const onSuggestionClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const text = e.currentTarget.dataset.text
    if (text) {
      setCurrentMessage(text)
    }
  }, [])

  const createFromOptionByIndex = useCallback(async(index: number) => {
    const option = options[index]
    const pf = option?.parsedFields
    if (!pf) return

    setCreatingOptionIndex(index)

    async function resolvePlaceId(place?: { name?: string, searchTerm?: string, secondaryText?: string, placeId?: string }): Promise<{ name: string, secondaryText?: string, placeId?: string, airportCode?: string, point?: { lat: number, lng: number } } | undefined> {
      if (!place?.name) return undefined
      if (place.placeId) return { ...place, name: place.name }
      const input = (place.searchTerm && place.searchTerm.trim().length > 0) ? place.searchTerm : place.name
      if (!input) return { ...place, name: place.name }

      try {
        const preds = await getGooglePlaceAutocomplete({ input })

        const first = preds?.[0]
        if (first) {
          let airportCode: string | undefined
          if (first.types?.includes('airport')) {
            // Extract from parentheses: "Sydney Airport (SYD)"
            const match = first.structured_formatting?.main_text?.match(/\(([A-Z]{3})\)/)
            airportCode = match?.[1]
          }

          const placeDetails = await getGooglePlaceDetail({
            placeId: first.place_id,
            fields: ['geometry', 'formatted_address'],
          })

          return {
            name: first.structured_formatting?.main_text ?? place.name,
            secondaryText: first.structured_formatting?.secondary_text ?? place.secondaryText,
            placeId: first.place_id,
            airportCode,
            point: placeDetails.geometry?.location ? {
              lat: placeDetails.geometry.location.lat,
              lng: placeDetails.geometry.location.lng,
            } : undefined,
          }
        }
      } catch {
        // Place ID resolution failed, fall through to return original place
      }
      return { ...place, name: place.name }
    }

    const [resolvedFrom, resolvedTo] = await Promise.all([
      resolvePlaceId(pf.fromPlace),
      resolvePlaceId(pf.toPlace),
    ])

    // Validate required places based on item type
    if (pf.itemType === 'FLIGHT' && (!resolvedFrom?.placeId || !resolvedTo?.placeId)) {
      setCreatingOptionIndex(null)
      showSnackbar(intl.formatMessage({
        id: 'OpMXsYYdnw7agNytZg5k',
        defaultMessage: 'Couldn’t find both airports. Try specifying IATA codes (e.g., SYD).',
        description: 'Error message when flight departure or arrival airport could not be resolved',
      }), 'warning')
      return
    }

    if (pf.itemType !== 'FLIGHT' && !resolvedTo?.placeId) {
      setCreatingOptionIndex(null)
      showSnackbar(intl.formatMessage({
        id: 'WK7aCmQlEefFooLMRHmd',
        defaultMessage: 'Couldn’t find that place. Try another venue or add a note.',
        description: 'Error message when a place location could not be found',
      }), 'warning')
      return
    }

    const fallbackParsedFields: ParsedFieldsInput = {
      ...pf,
      fromPlace: resolvedFrom ?? option.parsedFields?.fromPlace,
      toPlace: resolvedTo ?? option.parsedFields?.toPlace,
    }
    const untitledLabel = intl.formatMessage({
      id: 'kpUThS3iao0Cf-mEQl7F',
      defaultMessage: 'Untitled',
      description: 'Default name for a trip item when no name is provided',
    })
    const payload = mapParsedFieldsToRequest(fallbackParsedFields, untitledLabel)
    if (payload) {
      const tripItem = date ? { ...payload, startDate: date } : payload
      createTripItem({ tripId: trip.id, tripItem, rankSpec })
    } else {
      setCreatingOptionIndex(null)
    }
  }, [options, rankSpec, date, trip.id, createTripItem, intl])

  const createFromOption = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const index = parseInt(e.currentTarget.dataset.index ?? '', 10)
    if (!isNaN(index)) {
      createFromOptionByIndex(index)
    }
  }, [createFromOptionByIndex])

  const onInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' && !dropdownOpen && currentMessage.length > 0) {
      openDropdown()
    }
    if (e.key === 'Enter' && dropdownOpen) {
      if (options.length > 0) {
        e.preventDefault()
        createFromOptionByIndex(0)
      } else if (suggestions.length > 0) {
        e.preventDefault()
        setCurrentMessage(suggestions[0])
      }
    }
  }, [dropdownOpen, currentMessage.length, openDropdown, options, suggestions, createFromOptionByIndex])

  const onInputFocus = useCallback(() => {
    if (!dropdownOpen && currentMessage.length > 0) {
      openDropdown()
    }
  }, [currentMessage.length, openDropdown, dropdownOpen])

  const onAddNoteClick = () => {
    createTripItem({
      tripId: trip.id,
      tripItem: {
        type: 'NOTE',
        notes: currentMessage,
        sourceType: 'NA',
        startDate: date,
      },
      rankSpec,
    })
  }

  const lastSentAt = useRef<number | null>(null)
  useEffect(() => {
    if (currentMessage && currentMessage.trim().length >= 3) {
      lastSentAt.current = Date.now()
    } else {
      lastSentAt.current = null
    }
  }, [currentMessage])

  useEffect(() => {
    if (data && lastSentAt.current) {
      const latency = Date.now() - lastSentAt.current
      trackClientEvent({
        subject: 'trip_planner_ai_parse_success',
        action: String(latency),
        category: 'trip_planner',
        type: 'operational',
      })
    }
  }, [data])

  useEffect(() => {
    if (error) {
      const status = (error as { status?: number })?.status
      trackClientEvent({
        subject: 'trip_planner_ai_parse_error',
        action: String(status ?? 'unknown'),
        category: 'trip_planner',
        type: 'operational',
      })
      showSnackbar(intl.formatMessage({
        id: 'A1h9Bueq4wi-SF_Q5Z5d',
        defaultMessage: 'Couldn’t interpret your plan yet — you can still add a note.',
        description: 'Warning message when AI could not parse the user input into a trip item',
      }), 'warning')
    }
  }, [error, intl])

  const rootRef = useRef<HTMLDivElement>(null)
  const { viewState: { isModalOpen } } = useView()
  const placeholderText = intl.formatMessage({
    id: 'QwKQ9hco2ZhB8-yOve2D',
    defaultMessage: 'Add a trip item',
    description: 'Placeholder text in the trip planner input where users type a new itinerary item',
  })

  const optionId = (index: number) => `nlp-item-create-ai-option-${index}`
  const optionLabelsSet = useMemo(() => new Set(options.map((option) => (option.label || '').toLowerCase().trim())), [options])
  const remainingSuggestions = useMemo(() => suggestions.filter(s => !optionLabelsSet.has(s.toLowerCase().trim())), [suggestions, optionLabelsSet])

  useArrowListNavigation(
    [
      INPUT_ID,
      ...(options.length ? options.map((_option, index) => optionId(index)) : []),
      ...(!options.length ? remainingSuggestions.map((_suggestion: string, index: number) => getListItemId(index)) : []),
      ADD_NOTE_ID,
    ],
    {
      enabled: dropdownOpen,
      onRequestClose: closeDropdown,
      eventListenerTargetRef: rootRef,
      afterCloseFocusRef: inputElement,
    },
  )

  if (isCreatingTripItem) {
    return <LoadingIndicator inline />
  }

  return (
    <div ref={rootRef}>
      <InputWrapper>
        <TextInput
          ref={inputElement}
          defaultValue={currentMessage}
          onChange={onInputChange}
          onKeyDown={onInputKeyDown}
          onFocus={onInputFocus}
          endIcon={loading ? <LoadingIcon /> : <SearchIcon />}
          placeholder={placeholderText}
          noValidationSpacing
          id={INPUT_ID}
          autoComplete="off"
          disabled={isCreatingTripItem}
        />
        <StyledExampleFlipper />
      </InputWrapper>
      <DropdownList
        size="fill-anchor"
        anchorRef={inputElement}
        triggerRef={inputElement}
        open={dropdownOpen}
        onClose={closeDropdown}
        enableFlip={dropdownAutoHeight}
        autoHeight={dropdownAutoHeight}
        portaled={!isModalOpen}
      >
        {loading && (
          <ClickableListItem title={<LoadingAnimation />} />
        )}
        {!loading && options.map((option, index) => {
          const isCreatingThisOption = creatingOptionIndex === index
          const titleContent = option.parsedFields?.suggestedTitle
            ? option.parsedFields.suggestedTitle
            : (option.labelMarkdown ? <MarkdownRender content={option.labelMarkdown} /> : option.label)

          return (
            <ClickableListItem
              key={`opt-${index}`}
              title={isCreatingThisOption ? <LoadingAnimation /> : titleContent}
              subtitle={!isCreatingThisOption && option.secondaryLabel ? <PreSpan>{option.secondaryLabel}</PreSpan> : undefined}
              id={optionId(index)}
              data-index={index}
              onClick={createFromOption}
              disabled={isCreatingThisOption}
            />
          )
        })}
        {!loading && options.length === 0 && remainingSuggestions.map((text, index) => (
          <ClickableListItem
            key={`sugg-${index}`}
            title={text}
            id={getListItemId(index)}
            data-text={text}
            onClick={onSuggestionClick}
          />
        ))}
        <Hr />
        <ClickableListItem
          startIcon={<LineNotesIcon size="S" />}
          title={intl.formatMessage(
            {
              id: 's7gwQJrDMfcrWTcdIr-r',
              defaultMessage: 'Add note ‘{currentMessage}’',
              description: 'Button text to create a new note with the typed message content',
            },
            { currentMessage },
          )}
          id={ADD_NOTE_ID}
          onClick={onAddNoteClick}
        />
      </DropdownList>
    </div>
  )
}

export default NLPItemCreate
